import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { HttpRequestContext } from '../../../Blocks/HttpRequestProvider';

/**
 * Reusable BillingProfileForm component
 * @param {Object} props Component props
 * @param {Function} props.onSaveSuccess Callback function when profile is successfully saved
 * @param {string} props.buttonText Text to display on the submit button (default: "Enregistrer")
 * @param {Function} props.onCancel Optional callback function when user cancels/closes
 */
const BillingProfileForm = ({ onSaveSuccess, buttonText = "Enregistrer", onCancel }) => {
  const { sendRequest } = useContext(HttpRequestContext);
  const [formData, setFormData] = useState({
    address_line1: '',
    address_line2: '',
    bce_number: '',
    bic: '',
    company_name: '',
    email: '',
    iban: '',
    logo: null,
    vat_number: '',
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch billing profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await sendRequest({
          apiPath: '/invoicing/billingprofile',
          type: 'GET',
        });
        if (response?.properties) {
          const props = response.properties;
          setFormData({
            address_line1: props.address_line1?.value || '',
            address_line2: props.address_line2?.value || '',
            bce_number: props.bce_number?.value || '',
            bic: props.bic?.value || '',
            company_name: props.company_name?.value || '',
            email: props.email?.value || '',
            iban: props.iban?.value || '',
            logo: props.logo?.value || null,
            vat_number: props.vat_number?.value || '',
          });
          // Set logo preview if available
          if (props.logo?.value) {
            if (typeof props.logo.value === 'object' && props.logo.value.url) {
              setLogoPreview(props.logo.value.url);
            } else if (typeof props.logo.value === 'string') {
              setLogoPreview(props.logo.value);
            }
          }
        }
      } catch (err) {
        console.error(err);
        setError("Erreur lors de la récupération du profil de facturation.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [sendRequest]);

  // Handle changes in text fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle logo file selection and preview
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Save or update the billing profile
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      let payload;
      let headers = {};

      if (formData.logo instanceof File) {
        // When a new file is selected, use FormData
        payload = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          payload.append(key, value);
        });
      } else {
        // Otherwise, send as JSON
        payload = { ...formData };
        headers = { 'Content-Type': 'application/json' };
      }

      const response = await sendRequest({
        apiPath: '/invoicing/billingprofile',
        type: 'POST',
        request: payload,
        headers,
      });
      
      setSuccessMessage('Profil de facturation mis à jour avec succès!');
      if (onSaveSuccess && typeof onSaveSuccess === 'function') {
        onSaveSuccess(response);
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la mise à jour du profil.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom de l'Entreprise"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email de l'Entreprise"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adresse Ligne 1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adresse Ligne 2"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Numéro BCE"
                name="bce_number"
                value={formData.bce_number}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="BIC"
                name="bic"
                value={formData.bic}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="IBAN"
                name="iban"
                value={formData.iban}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Numéro TVA"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            {/* Logo upload section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Logo de l'Entreprise
              </Typography>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Button variant="contained" component="label">
                  {logoPreview ? 'Changer le logo' : 'Télécharger le logo'}
                  <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                </Button>
                {logoPreview && (
                  <Avatar src={logoPreview} variant="rounded" sx={{ width: 80, height: 80 }} />
                )}
              </Box>
            </Grid>

            {/* Display error or success messages */}
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            {successMessage && (
              <Grid item xs={12}>
                <Typography color="success.main">{successMessage}</Typography>
              </Grid>
            )}
          </Grid>
          
          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            {onCancel && (
              <Button 
                variant="outlined" 
                onClick={onCancel}
              >
                Annuler
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 120 }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : buttonText}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BillingProfileForm; 